'use client'

import cx from 'classix'
import dynamic from 'next/dynamic'
import { useLayoutEffect, useRef, useState } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import { applyStylesIf } from 'utils/applyStylesIf/applyStylesIf'

import {
	type Cta,
	HERO_BANNER_SHOP_PLUS_TITLE_TYPE,
	type TitleImage,
} from '../../../../../../Home.type'
import textStyles from '../../../../TextStyles.module.css'
import { useTitleImageHeightOverflow } from '../../hooks/useTitleImageHeightOverflow'
import styles from './HeroBannerShopContent.module.css'

const HeroBannerShopCtas = dynamic(() =>
	import('../HeroBannerShopCtas/HeroBannerShopCtas').then(
		(mod) => mod.HeroBannerShopCtas
	)
)

const HeroBannerShopTitle = dynamic(() =>
	import('../HeroBannerShopTitle/HeroBannerShopTitle').then(
		(mod) => mod.HeroBannerShopTitle
	)
)

const HeroBannerShopTitleImage = dynamic(() =>
	import('../HeroBannerShopTitleImage/HeroBannerShopTitleImage').then(
		(mod) => mod.HeroBannerShopTitleImage
	)
)

interface HeroBannerShopContentProps {
	readonly titleType: HERO_BANNER_SHOP_PLUS_TITLE_TYPE | null
	readonly titleKey: string | null
	readonly titleKeyLanguage: string | null
	readonly titleImage: TitleImage | null
	readonly mobileTitleImage: TitleImage | null
	readonly inverse: boolean
	readonly sectionCtas: Cta[]
}

const CTAS_HEIGHT = 60

export function HeroBannerShopContent({
	titleType,
	titleKey,
	titleKeyLanguage,
	titleImage,
	mobileTitleImage,
	inverse,
	sectionCtas,
}: HeroBannerShopContentProps) {
	const contentWrapperRef = useRef<HTMLDivElement>(null)
	const { isLargeOrGreater } = useResponsive()
	const isTitleImage =
		titleType === HERO_BANNER_SHOP_PLUS_TITLE_TYPE.IMAGE &&
		titleImage &&
		mobileTitleImage
	const applyImageHeightOverflow = isTitleImage && !isLargeOrGreater
	const [isFirstRender, setIsFirstRender] = useState(true)

	const { isImageHeightOverflowing, maxHeight } = useTitleImageHeightOverflow({
		imageWrapperRef: contentWrapperRef,
		imageAspectRatio: applyImageHeightOverflow
			? mobileTitleImage.aspectRatio
			: null,
		imageRelativeWidth: applyImageHeightOverflow
			? mobileTitleImage.mobileWidth
			: null,
		padding: CTAS_HEIGHT,
	})

	useLayoutEffect(() => {
		setIsFirstRender(false)
	}, [])

	return (
		<div
			className={cx(
				styles.heroBannerTextCenterWrapper,
				applyStylesIf(Boolean(isTitleImage), styles.titleImageContainer),
				applyStylesIf(!isTitleImage && inverse, textStyles.textShadow)
			)}
			ref={contentWrapperRef}
		>
			{isFirstRender ? null : (
				<>
					{isTitleImage ? (
						<HeroBannerShopTitleImage
							titleImage={titleImage}
							mobileTitleImage={mobileTitleImage}
							isLargeOrGreater={isLargeOrGreater}
							isImageHeightOverflowing={isImageHeightOverflowing}
							maxHeight={maxHeight}
						/>
					) : (
						<HeroBannerShopTitle
							titleType={titleType}
							titleKey={titleKey}
							titleKeyLanguage={titleKeyLanguage}
							inverse={inverse}
							isLargeOrGreater={isLargeOrGreater}
						/>
					)}
					<HeroBannerShopCtas sectionCtas={sectionCtas} inverse={inverse} />
				</>
			)}
		</div>
	)
}
